import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  createAttributeGroup,
  deleteAttributeGroup,
  listAttributeGroupRecords,
  syncAttributeGroupsFromApi,
  updateAttributeGroup,
  type AttributeGroupRecord,
} from "@/lib/catalog/attributeGroupsStore";
import { attributeCountForGroup } from "@/lib/catalog/catalogAttributesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { slugify } from "@/lib/slugify";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Eye, Filter, Layers, LayoutGrid, List, ListOrdered, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";

const ATTRIBUTE_GROUPS_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatAgDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type AgSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "sort_asc"
  | "sort_desc";

const AttributeGroups = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "attribute_group_can_view");
  const canCreate = can(user, "attribute_group_can_create");
  const canUpdate = can(user, "attribute_group_can_update");
  const canDelete = can(user, "attribute_group_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<AgSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [filterDesc, setFilterDesc] = useState("");
  const [filterMinSortOrder, setFilterMinSortOrder] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AttributeGroupRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AttributeGroupRecord | null>(null);
  const [viewGroup, setViewGroup] = useState<AttributeGroupRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [isSaving, setIsSaving] = useState(false);

  const groups = useMemo(() => listAttributeGroupRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsForbidden(false);
      try {
        await syncAttributeGroupsFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[AttributeGroups] Load failed", error);
        if (mounted) {
          setInfoDialog({
            title: "Unable to load groups",
            description: "Could not fetch attribute groups from API. Showing cached data if available.",
          });
        }
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const slugNeedle = filterSlug.trim().toLowerCase();
    const descNeedle = filterDesc.trim().toLowerCase();
    const minSo = Number(filterMinSortOrder);
    const hasMinSo = filterMinSortOrder.trim() !== "" && Number.isFinite(minSo);

    return groups.filter((g) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(g.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(g.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (nameNeedle && !g.name.toLowerCase().includes(nameNeedle)) return false;
      if (slugNeedle && !g.slug.toLowerCase().includes(slugNeedle)) return false;
      if (descNeedle && !(g.description?.toLowerCase().includes(descNeedle) ?? false)) return false;
      if (hasMinSo && (g.sortOrder ?? 0) < minSo) return false;

      const matchesQuery =
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.slug.toLowerCase().includes(q) ||
        (g.description?.toLowerCase().includes(q) ?? false) ||
        String(g.sortOrder ?? "").includes(q);
      return matchesQuery;
    });
  }, [groups, query, filterCreatedDate, quickDate, filterName, filterSlug, filterDesc, filterMinSortOrder]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "sort_desc":
          return (b.sortOrder ?? 0) - (a.sortOrder ?? 0);
        case "sort_asc":
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterSlug.trim() ? 1 : 0) +
    (filterDesc.trim() ? 1 : 0) +
    (filterMinSortOrder.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterSlug("");
    setFilterDesc("");
    setFilterMinSortOrder("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterSlug}|${filterDesc}|${filterMinSortOrder}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: ATTRIBUTE_GROUPS_PAGE_SIZE_OPTIONS,
    defaultPageSize: ATTRIBUTE_GROUPS_PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setSortOrder(String(groups.reduce((m, g) => Math.max(m, g.sortOrder), 0) + 1));
    setOpen(true);
  };

  const startEdit = (g: AttributeGroupRecord) => {
    setEditing(g);
    setName(g.name);
    setDescription(g.description ?? "");
    setSortOrder(String(g.sortOrder));
    setOpen(true);
  };

  const resetCreateForm = () => {
    setName("");
    setDescription("");
    setSortOrder(String(groups.reduce((m, g) => Math.max(m, g.sortOrder), 0) + 1));
  };

  const save = async () => {
    if (!name.trim() || isSaving) return;
    setIsSaving(true);
    try {
      if (editing) {
        const updateResult = await updateAttributeGroup(editing.id, {
          name,
          slug: slugify(name),
          description: description.trim() || null,
          sortOrder: Number(sortOrder) || 0,
        });
        if (updateResult?.message) {
          toast.success(updateResult.message);
        }
      } else {
        const createResult = await createAttributeGroup({
          name,
          slug: slugify(name),
          description: description.trim() || null,
          sortOrder: Number(sortOrder) || 0,
        });
        resetCreateForm();
        toast.success(createResult.message);
      }
      if (editing) {
        setOpen(false);
      }
      refresh();
    } catch (error) {
      console.error("[AttributeGroups] Save failed", error);
      setInfoDialog({
        title: "Unable to save group",
        description: "The group could not be saved. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (g: AttributeGroupRecord) => {
    if (attributeCountForGroup(g.id) > 0) {
      setInfoDialog({
        title: "Cannot delete group",
        description: `“${g.name}” still has attributes. Remove or reassign them on the Attributes page first.`,
      });
      return;
    }
    setDeleteTarget(g);
  };

  const confirmRemoveGroup = async () => {
    if (!deleteTarget) return;
    try {
      const result = await deleteAttributeGroup(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[AttributeGroups] Delete failed", error);
      toast.error("Unable to delete group. Please try again.");
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Attribute groups access required"
      message="You don’t have permission to view attribute groups. Ask an administrator to grant attribute_group_can_view_any."
    />
  ) : (
    <Layout title="Attribute groups">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Attribute groups</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Sections shown on product forms (e.g. Basic specs, Documents). Then add{" "}
              <Link to="/attributes" className="text-primary hover:underline font-medium">
                attributes
              </Link>{" "}
              inside each group.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add group
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter attribute groups</SheetTitle>
                  <SheetDescription>Refine by name, slug, description, sort order, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Attribute group</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Basic"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Slug contains</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={filterSlug}
                        onChange={(e) => setFilterSlug(e.target.value)}
                        placeholder="e.g. basic-specs"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Description contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterDesc}
                        onChange={(e) => setFilterDesc(e.target.value)}
                        placeholder="e.g. engine"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Minimum sort order</Label>
                    <Input
                      className="glass-input h-10"
                      type="number"
                      value={filterMinSortOrder}
                      onChange={(e) => setFilterMinSortOrder(e.target.value)}
                      placeholder="e.g. 1"
                    />
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="attr-groups-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={ATTRIBUTE_GROUPS_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as AgSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="sort_asc">Sort order: Low to high</SelectItem>
                  <SelectItem value="sort_desc">Sort order: High to low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="attr-groups-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={ATTRIBUTE_GROUPS_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AgSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="sort_asc">Sort order: Low to high</SelectItem>
                    <SelectItem value="sort_desc">Sort order: High to low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit group" : "New group"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Name *</Label>
                <Input className="glass-input h-10" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Basic specs" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Additional information (description)</Label>
                <Textarea
                  className="glass-input min-h-[88px] resize-y"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Shown under the group title on the add-product specification step…"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Sort order</Label>
                <Input className="glass-input h-10" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewGroup} onOpenChange={(o) => !o && setViewGroup(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Attribute group details</DialogTitle>
            </DialogHeader>
            {viewGroup ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Name</dt>
                  <dd className="font-medium text-foreground">{viewGroup.name}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Slug</dt>
                  <dd className="text-muted-foreground font-mono">{viewGroup.slug}</dd>
                </div>
                {viewGroup.description?.trim() ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">Description</dt>
                    <dd className="text-muted-foreground whitespace-pre-wrap">{viewGroup.description}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs text-muted-foreground">Sort order</dt>
                  <dd className="text-muted-foreground">{viewGroup.sortOrder}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Attributes in group</dt>
                  <dd className="text-muted-foreground">
                    {viewGroup.attributesCount ?? attributeCountForGroup(viewGroup.id)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatAgDate(viewGroup.createdAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewGroup(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {groups.length === 0 ? (
              canCreate ? (
                <>
                  No groups yet. Create one, then add attributes on the{" "}
                  <Link to="/attributes" className="text-primary font-medium hover:underline">
                    Attributes
                  </Link>{" "}
                  page.
                </>
              ) : (
                <>
                  No groups yet. Add attributes on the{" "}
                  <Link to="/attributes" className="text-primary font-medium hover:underline">
                    Attributes
                  </Link>{" "}
                  page once groups exist.
                </>
              )
            ) : (
              "No groups match your search."
            )}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={g.name}
                  primaryMeta={{
                    label: "Sort order",
                    value: String(g.sortOrder),
                    icon: ListOrdered,
                    iconClassName: "text-emerald-600",
                  }}
                  secondaryMeta={{
                    label: "Attributes",
                    value: String(g.attributesCount ?? attributeCountForGroup(g.id)),
                    icon: Layers,
                    iconClassName: "text-red-600",
                  }}
                  footer={
                    g.description?.trim()
                      ? `${g.description.trim()} · Slug: ${g.slug} · Created: ${formatCatalogAuditDate(g.createdAt)}`
                      : `Slug: ${g.slug} · Created: ${formatCatalogAuditDate(g.createdAt)}`
                  }
                  onView={canView ? () => setViewGroup(g) : undefined}
                  onEdit={canUpdate ? () => startEdit(g) : undefined}
                  onDelete={canDelete ? () => requestRemove(g) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Layers className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{g.name}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600">
                        Sort {g.sortOrder}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate font-mono">{g.slug}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {g.attributesCount ?? attributeCountForGroup(g.id)} attributes · Created {formatCatalogAuditDate(g.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewGroup(g)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => startEdit(g)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => requestRemove(g)}
                        aria-label="Delete group"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this attribute group?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemoveGroup()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default AttributeGroups;
